import { css, keyframes } from '@emotion/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { darkTheme } from '@/theme/dark-theme';
import { fontFamilies, fontSizes, glass, mq, radii, shadows, spacing } from '@/theme/tokens';

const VALID_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const CODE_LENGTH = 8;

export interface LandingPageProps {
  onJoin: (code: string) => void;
  initialCode?: string | null;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

function sanitizeCode(value: string): string {
  return value
    .toUpperCase()
    .split('')
    .filter((c) => VALID_CHARS.includes(c))
    .join('')
    .slice(0, CODE_LENGTH);
}

export function LandingPage({ onJoin, initialCode }: LandingPageProps) {
  const [code, setCode] = useState(initialCode ? sanitizeCode(initialCode) : '');
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isValid = code.length === CODE_LENGTH;

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (isValid) {
        onJoin(code);
      }
    },
    [code, isValid, onJoin],
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(sanitizeCode(e.target.value));
  }, []);

  const stopCamera = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop();
      }
      streamRef.current = null;
    }
    setScanning(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    setScanning(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      // Start QR scanning loop
      const scanFrame = async () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
          rafRef.current = requestAnimationFrame(scanFrame);
          return;
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          rafRef.current = requestAnimationFrame(scanFrame);
          return;
        }

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        try {
          const jsQR = (await import('jsqr')).default;
          const qrResult = jsQR(imageData.data, imageData.width, imageData.height);

          if (qrResult?.data) {
            // Try to extract code from URL
            try {
              const url = new URL(qrResult.data);
              const codeParam = url.searchParams.get('code');
              if (codeParam) {
                stopCamera();
                onJoin(sanitizeCode(codeParam));
                return;
              }
            } catch {
              // Not a URL, try as raw code
              const sanitized = sanitizeCode(qrResult.data);
              if (sanitized.length === CODE_LENGTH) {
                stopCamera();
                onJoin(sanitized);
                return;
              }
            }
          }
        } catch {
          // jsQR decode error, continue scanning
        }

        rafRef.current = requestAnimationFrame(scanFrame);
      };

      rafRef.current = requestAnimationFrame(scanFrame);
    } catch {
      setCameraError('Camera access denied. Please allow camera access to scan QR codes.');
      setScanning(false);
    }
  }, [onJoin, stopCamera]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  // Auto-focus input
  useEffect(() => {
    if (!scanning) {
      inputRef.current?.focus();
    }
  }, [scanning]);

  return (
    <div
      css={css`
        min-height: 100vh;
        background: ${darkTheme.background};
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: ${spacing.lg};
        animation: ${fadeIn} 0.3s ease-out;
      `}
    >
      {/* Logo / Wordmark */}
      <h1
        css={css`
          color: ${darkTheme.text};
          font-family: ${fontFamilies.display};
          font-size: ${fontSizes.xxl};
          font-weight: 700;
          margin: 0 0 ${spacing.xs};
          letter-spacing: -0.02em;

          ${mq.sm} {
            font-size: ${fontSizes.xxxl};
          }
        `}
      >
        Sonematic
      </h1>
      <p
        css={css`
          color: ${darkTheme.textSecondary};
          font-size: ${fontSizes.sm};
          margin: 0 0 ${spacing.xl};
          text-align: center;
        `}
      >
        Enter the code shown on the presenter&apos;s screen
      </p>

      {/* QR Scanner overlay */}
      {scanning && (
        <div
          css={css`
            position: fixed;
            inset: 0;
            z-index: 50;
            background: ${darkTheme.background};
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          `}
        >
          <div
            css={css`
              position: relative;
              width: min(80vw, 400px);
              aspect-ratio: 1;
              border-radius: ${radii.lg};
              overflow: hidden;
              border: 2px solid ${darkTheme.primary};
            `}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              css={css`
                width: 100%;
                height: 100%;
                object-fit: cover;
              `}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
          <p
            css={css`
              color: ${darkTheme.textSecondary};
              font-size: ${fontSizes.sm};
              margin: ${spacing.md} 0;
            `}
          >
            Point your camera at the QR code
          </p>
          <button
            type="button"
            onClick={stopCamera}
            css={css`
              background: ${darkTheme.surface};
              color: ${darkTheme.text};
              border: 1px solid ${darkTheme.border};
              padding: 12px ${spacing.xl};
              border-radius: ${radii.md};
              font-size: ${fontSizes.md};
              cursor: pointer;
              min-height: 44px;

              &:hover {
                background: ${darkTheme.border};
              }
            `}
          >
            Close
          </button>
        </div>
      )}

      {/* Code entry form */}
      <form
        onSubmit={handleSubmit}
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: ${spacing.md};
          width: 100%;
          max-width: 360px;
        `}
      >
        <input
          ref={inputRef}
          type="text"
          value={code}
          onChange={handleChange}
          placeholder="ENTER CODE"
          maxLength={CODE_LENGTH}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
          aria-label="Session code"
          css={css`
            width: 100%;
            padding: ${spacing.md} ${spacing.lg};
            font-size: ${fontSizes.xxl};
            font-family: ${fontFamilies.mono};
            font-weight: 700;
            letter-spacing: 0.2em;
            text-align: center;
            background: ${darkTheme.surface};
            color: ${darkTheme.text};
            border: 2px solid ${darkTheme.border};
            border-radius: ${radii.sm};
            outline: none;
            transition: border-color 0.15s ease;

            &:focus {
              border-color: ${darkTheme.primary};
            }

            &::placeholder {
              color: ${darkTheme.textMuted};
              letter-spacing: 0.15em;
            }
          `}
        />

        <button
          type="submit"
          disabled={!isValid}
          css={css`
            width: 100%;
            padding: ${spacing.md};
            font-size: ${fontSizes.md};
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.2px;
            background: ${isValid ? darkTheme.buttonBg : darkTheme.border};
            color: ${isValid ? darkTheme.primaryText : darkTheme.textMuted};
            border: 1px solid ${darkTheme.buttonBorder};
            border-radius: ${radii.button};
            box-shadow: ${shadows.insetButton};
            cursor: ${isValid ? 'pointer' : 'not-allowed'};
            min-height: 48px;
            transition: all 0.15s ease;

            &:hover:not(:disabled) {
              background: ${darkTheme.primaryHover};
            }
          `}
        >
          Join
        </button>

        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.md};
            width: 100%;
          `}
        >
          <hr
            css={css`
              flex: 1;
              border: none;
              border-top: 1px solid ${darkTheme.border};
            `}
          />
          <span
            css={css`
              color: ${darkTheme.textSecondary};
              font-size: ${fontSizes.xs};
              text-transform: uppercase;
              letter-spacing: 0.1em;
            `}
          >
            or
          </span>
          <hr
            css={css`
              flex: 1;
              border: none;
              border-top: 1px solid ${darkTheme.border};
            `}
          />
        </div>

        <button
          type="button"
          onClick={startCamera}
          css={css`
            width: 100%;
            padding: ${spacing.md};
            font-size: ${fontSizes.md};
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.2px;
            background: ${glass.bg};
            backdrop-filter: ${glass.backdrop};
            color: ${darkTheme.text};
            border: 1px solid ${darkTheme.border};
            border-radius: ${radii.lg};
            cursor: pointer;
            min-height: 48px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: ${spacing.sm};
            transition: all 0.15s ease;

            &:hover {
              border-color: ${darkTheme.text};
            }
          `}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M17 12v4a1 1 0 0 1-1 1h-4" />
            <path d="M17 3h2a2 2 0 0 1 2 2v2" />
            <path d="M17 8V7" />
            <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
            <path d="M3 7V5a2 2 0 0 1 2-2h2" />
            <path d="M7 17h.01" />
            <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
            <rect x="7" y="7" width="5" height="5" rx="1" />
          </svg>
          Scan QR Code
        </button>

        {cameraError && (
          <p
            role="alert"
            css={css`
              color: ${darkTheme.error};
              font-size: ${fontSizes.sm};
              margin: 0;
              text-align: center;
            `}
          >
            {cameraError}
          </p>
        )}
      </form>
    </div>
  );
}
